import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdModeEdit, MdDeleteOutline } from "react-icons/md";
import toast from "react-hot-toast";
import client from "../api/client";
import PropertyVisibilityManager from "./PropertyVisibilityManager";
import SurveyGroupAssignment from "./SurveyGroupAssignment";
import FilterConfigManager from "./FilterConfigManager";
import TenantUsersManager from "./TenantUsersManager";
import ConfirmDialog from "./ConfirmDialog";
import TableSkeleton from "./TableSkeleton";
import SpinnerLoader from "./SpinnerLoader";
import { withMinDuration, reloadIfCurrentTenant } from "../utils";
import { isRegionalManager } from "../constants/roles";

const STATUS_VALUES = ["active", "suspended", "pending"];
const PAGE_SIZE = 10;

const TABS = [
  { key: "tenants", label: "Tenants" },
  { key: "users", label: "Users" },
  { key: "propertyVisibility", label: "Property Visibility" },
  { key: "surveyGroups", label: "Survey Groups" },
  { key: "filterProperties", label: "Filter Properties" },
];

const REGIONAL_MANAGER_TABS = ["users", "surveyGroups", "filterProperties"];

const getStoredTenantId = () => {
  const id = Number(localStorage.getItem("currentTenantId"));
  return Number.isFinite(id) && id > 0 ? id : null;
};

// Reuses the same table-rendering conventions as UserTableDirectory.js/
// ContactTableDatabase.js (TableSkeleton loading state, MdModeEdit edit trigger)
// rather than a new table pattern. Create/edit live on their own route
// (TenantForm.js) instead of a modal here. Delete is a soft delete
// (status: "deleted") server-side — listTenants already excludes deleted
// tenants, so a successful delete just needs to drop the row locally.
const TenantManagement = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [isRM] = useState(isRegionalManager);
  const [currentTenantId] = useState(getStoredTenantId);
  const visibleTabs = isRM ? TABS.filter((tab) => REGIONAL_MANAGER_TABS.includes(tab.key)) : TABS;
  const [activeTab, setActiveTab] = useState(visibleTabs[0].key);
  const [tenantPendingDelete, setTenantPendingDelete] = useState(null);
  const [actioningId, setActioningId] = useState(null);
  const [selectedTenantIdForUsers, setSelectedTenantIdForUsers] = useState(currentTenantId);
  const [selectedTenantIdForPropertyVisibility, setSelectedTenantIdForPropertyVisibility] = useState(currentTenantId);
  const [selectedTenantIdForSurveyGroups, setSelectedTenantIdForSurveyGroups] = useState(currentTenantId);
  const [selectedTenantIdForFilterProperties, setSelectedTenantIdForFilterProperties] = useState(currentTenantId);
  // createTenant/deleteTenant permission is computed server-side by GET
  // /tenants (same isGlobalSuperAdmin/ROLE_PERMISSIONS check the write endpoints
  // enforce) instead of inferred from the cached userRole/isSuperAdmin localStorage
  // strings here — those are only refreshed on login/tenant switch and can drift
  // out of sync with the actual role, which let regional_manager/account_manager
  // keep seeing the New Tenant/delete buttons.
  const [capabilities, setCapabilities] = useState({ canCreate: false, canDelete: false });

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const res = await withMinDuration(client.get("/tenants"));
      setTenants(res.data.tenants);
      setCapabilities(res.data.capabilities || { canCreate: false, canDelete: false });
      setPage(1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load tenants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const defaultTenantId = tenants.some((t) => t.id === currentTenantId) ? currentTenantId : tenants[0]?.id ?? null;
  // Known from the stored role before /tenants returns, so restricted controls never flash for a regional manager.
  const lockedTenant = isRM ? tenants.find((t) => t.id === currentTenantId) || { id: currentTenantId, name: "" } : null;

  useEffect(() => {
    if (defaultTenantId == null) return;
    const fill = (prev) => (prev != null && tenants.some((t) => t.id === prev) ? prev : defaultTenantId);
    setSelectedTenantIdForUsers(fill);
    setSelectedTenantIdForPropertyVisibility(fill);
    setSelectedTenantIdForSurveyGroups(fill);
    setSelectedTenantIdForFilterProperties(fill);
  }, [tenants, defaultTenantId]);

  const pageCount = Math.max(1, Math.ceil(tenants.length / PAGE_SIZE));
  const pagedTenants = tenants.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleStatusChange = async (tenant, status) => {
    if (actioningId) return;
    setActioningId(tenant.id);
    try {
      const res = await client.patch(`/tenants/${tenant.id}/status`, { status });
      setTenants((prev) => prev.map((t) => (t.id === tenant.id ? res.data : t)));
      toast.success(`Tenant status set to ${status}.`);
      reloadIfCurrentTenant(tenant.id);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status.");
    } finally {
      setActioningId(null);
    }
  };

  const handleConfirmDelete = async () => {
    const tenant = tenantPendingDelete;
    if (actioningId) return;
    setActioningId(tenant.id);
    try {
      await client.patch(`/tenants/${tenant.id}/status`, { status: "deleted" });
      setTenants((prev) => prev.filter((t) => t.id !== tenant.id));
      toast.success(`${tenant.name} deleted.`);
      setTenantPendingDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete tenant.");
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="bg-canvas-userbar w-full px-6 py-8">
      <div className="bg-canvas-white rounded">
        <div className="flex flex-col p-8 gap-3 rounded">
          <h2>Tenant Management{lockedTenant?.name ? ` - ${lockedTenant.name}` : ""}</h2>
          <h5 className="text-canvas-directoryText">
            Create and manage tenants, their property visibility, and survey group assignments.
          </h5>
        </div>

        <div role="tablist" className="flex items-center gap-1 px-8 border-b border-canvas-tersery/30">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`font-outfit text-sm font-semibold uppercase tracking-wide px-5 py-3 border-b-2 transition-colors ${
                activeTab === tab.key
                  ? "border-canvas-primary text-canvas-primary"
                  : "border-transparent text-canvas-secondery hover:text-canvas-primary/70"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "tenants" && !isRM && (
          <div className="flex flex-col">
            {capabilities.canCreate && (
              <div className="flex items-center justify-end px-8 pt-6">
                <button
                  type="button"
                  className="font-outfit text-[12px] uppercase font-bold bg-canvas-primary text-canvas-white py-3 px-6 rounded-[37px]"
                  onClick={() => navigate("/tenant-management/new")}
                >
                  + New Tenant
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <div className="p-1.5 min-w-full inline-block align-middle">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th scope="col" className="md:px-5 px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Name</th>
                        <th scope="col" className="md:px-6 px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Slug</th>
                        <th scope="col" className="md:px-6 px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Subdomain</th>
                        <th scope="col" className="md:px-6 px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Status</th>
                        <th scope="col" className="md:px-6 px-3 py-3 text-start text-xs text-canvas-secondery font-outfit font-bold uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 transition-opacity duration-200 ease-in" key={loading ? "loading" : "loaded"}>
                      {loading ? (
                        <TableSkeleton rows={4} columns={5} />
                      ) : (
                        pagedTenants.map((tenant) => (
                          <tr className="hover:bg-gray-100" key={tenant.id}>
                            <td className="md:px-5 px-3 md:py-4 py-2 whitespace-nowrap">{tenant.name}</td>
                            <td className="md:px-6 px-3 md:py-4 py-2 whitespace-nowrap font-outfit text-sm">{tenant.slug}</td>
                            <td className="md:px-6 px-3 md:py-4 py-2 whitespace-nowrap font-outfit text-sm">{tenant.subdomain}</td>
                            <td className="md:px-6 px-3 md:py-4 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <select
                                  className="border border-canvas-tersery rounded p-2 text-sm font-outfit disabled:opacity-50"
                                  value={tenant.status}
                                  onChange={(e) => handleStatusChange(tenant, e.target.value)}
                                  disabled={actioningId === tenant.id || tenant.is_default}
                                  title={tenant.is_default ? "The default tenant's status cannot be changed." : undefined}
                                >
                                  {STATUS_VALUES.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                                {actioningId === tenant.id && <SpinnerLoader className="w-4 h-4 text-canvas-secondery animate-spin fill-canvas-primary" />}
                              </div>
                            </td>
                            <td className="md:px-6 px-3 md:py-4 py-2 whitespace-nowrap flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => navigate(`/tenant-management/${tenant.id}/edit`)}
                                aria-label={`Edit ${tenant.name}`}
                              >
                                <MdModeEdit className="w-6 h-6 text-canvas-secondary" />
                              </button>
                              {capabilities.canDelete && (
                                <button
                                  type="button"
                                  onClick={() => setTenantPendingDelete(tenant)}
                                  disabled={actioningId === tenant.id}
                                  aria-label={`Delete ${tenant.name}`}
                                  className="disabled:opacity-50"
                                >
                                  <MdDeleteOutline className="w-6 h-6 text-canvas-danger" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {!loading && tenants.length > 0 && (
              <div className="flex items-center justify-between px-8 py-4">
                <span className="text-xs text-canvas-secondery font-outfit">
                  Page {page} of {pageCount} ({tenants.length} tenants)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-xs font-outfit font-bold uppercase border border-canvas-tersery rounded disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page === pageCount}
                    className="px-3 py-1.5 text-xs font-outfit font-bold uppercase border border-canvas-tersery rounded disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab !== "tenants" && loading && (
          <div className="flex justify-center py-16" role="status" aria-label="Loading tenant data">
            <SpinnerLoader className="w-8 h-8 text-canvas-secondery animate-spin fill-canvas-primary" />
          </div>
        )}

        {activeTab === "users" && !loading && (
          <TenantUsersManager
            tenantId={selectedTenantIdForUsers}
            tenants={tenants}
            onTenantChange={setSelectedTenantIdForUsers}
            lockedTenant={lockedTenant}
          />
        )}

        {activeTab === "propertyVisibility" && !isRM && !loading && (
          <PropertyVisibilityManager
            tenantId={selectedTenantIdForPropertyVisibility}
            tenants={tenants}
            onTenantChange={setSelectedTenantIdForPropertyVisibility}
          />
        )}

        {activeTab === "surveyGroups" && !loading && (
          <SurveyGroupAssignment
            tenantId={selectedTenantIdForSurveyGroups}
            tenants={tenants}
            onTenantChange={setSelectedTenantIdForSurveyGroups}
            lockedTenant={lockedTenant}
          />
        )}

        {activeTab === "filterProperties" && !loading && (
          <FilterConfigManager
            tenantId={selectedTenantIdForFilterProperties}
            tenants={tenants}
            onTenantChange={setSelectedTenantIdForFilterProperties}
            lockedTenant={lockedTenant}
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(tenantPendingDelete)}
        title="Delete tenant"
        message={`Delete ${tenantPendingDelete?.name}? This removes it from the tenant list and blocks its members from logging in. This can be undone by an app admin.`}
        confirmLabel="Delete"
        danger
        isConfirming={actioningId === tenantPendingDelete?.id}
        onConfirm={handleConfirmDelete}
        onCancel={() => setTenantPendingDelete(null)}
      />
    </div>
  );
};

export default TenantManagement;
